import isNode from 'is-node'
import localForage from 'localforage'
import { observable, action, computed, reaction, toJS } from 'mobx'

import config from 'src/config'
import countries from 'src/json/countries.json'
import {
  createCart,
  addItem,
  refreshCart,
  removeItem,
  updateItemQuantity,
  changeCountry,
  loginCentraUser,
  setVoucher,
} from 'src/utils/api'
import { storageKeys } from 'src/constants'
import { getLocaleByHost } from 'src/utils/intl'

const NODE_COUNTRY = 'US'

type CartType = null | {
  token: string
  location: {
    country: string
  }
  selection: {
    items: any[]
  }
}

export default class Cart {
  @observable cart: CartType = null
  @observable loading: boolean = false
  @observable error: boolean = false

  constructor() {
    if (!isNode) {
      localForage.getItem(storageKeys.CART).then(async (cart: any) => {
        if (cart) {
          this.updateCart(JSON.parse(cart))
          this.updateCart(await refreshCart(this.token))
        } else {
          this.updateCart(await createCart())
        }
        const country = getLocaleByHost(window.location.host)
        if (country) {
          const { centraCountry } = config.locales[country]
          this.setCountry(centraCountry)
        }
      })
      reaction(
        () => this.cart,
        (nextCart: CartType) => {
          // console.log('Updated cart: ', toJS(nextCart))
          localForage.setItem(storageKeys.CART, JSON.stringify(nextCart))
        }
      )
    }
  }

  async addItem(item: string) {
    this.setCartLoading()
    this.updateCart(await addItem(this.token, item))
  }

  async removeItem(item: string) {
    this.setCartLoading()
    this.updateCart(await removeItem(this.token, item))
  }

  async updateQuantity(line: string, quantity: string) {
    this.setCartLoading()
    this.updateCart(await updateItemQuantity(this.token, line, quantity))
  }

  @action.bound
  async voucher(code: string) {
    this.updateCart(await setVoucher(this.token, code))
  }

  @action.bound
  setCartLoading() {
    this.loading = true
  }

  @action.bound
  updateCart(cart: CartType) {
    if (cart instanceof Error) {
      this.setError(true)
      setTimeout(() => {
        this.setError(false)
      }, 5000)
    } else {
      this.loading = false
      this.cart = cart
    }
  }

  @action.bound
  setError(status: boolean) {
    this.error = status
  }

  @action.bound
  async setCountry(country: string) {
    this.updateCart(await changeCountry(this.token, country))
  }

  @action.bound
  async clear() {
    // TODO: do this correctly
    this.updateCart(await createCart())
  }

  @action.bound
  async loginUser(email: string, password: string) {
    const response = await loginCentraUser(this.token, email, password)
    if (response) {
      this.updateCart(response)
    } else {
      return false
    }
  }

  @computed
  get count(): number {
    if (!this.cart) {
      return 0
    }
    if (!this.cart.selection) {
      return 0
    }
    return this.cart.selection.items
      .map((item: any) => item.quantity)
      .reduce((a, b) => a + b, 0)
  }

  @computed
  get items(): object[] {
    if (!this.cart) {
      return []
    }
    if (!this.cart.selection) {
      return []
    }
    return this.cart.selection.items
  }

  @computed
  get token(): string {
    return this.cart ? this.cart.token : ''
  }

  @computed
  get country(): string | null {
    if (isNode) {
      return NODE_COUNTRY
    }
    return this.cart ? this.cart.location.country : null
  }

  @computed
  get selectedCountry(): object | null {
    if (!countries) return null
    if (this.cart && this.cart.location.country) {
      const filteredCountries = countries
        .filter(
          (country: any) => country.country === this.cart.location.country
        )
        .map((country: any) => ({
          value: country.country,
          label: country.name,
        }))
      if (filteredCountries && filteredCountries.length > 0) {
        return filteredCountries[0]
      }
    }
    return null
  }

  @computed
  get allCountries(): object[] | null {
    if (!countries) return null
    const response = countries
      .filter((country: any) => country.shipTo)
      .map((country: any) => ({
        value: country.country,
        label: country.name,
      }))
    return response
  }

  async refresh() {
    this.updateCart(await refreshCart(this.token))
  }
}
